<template>
  <v-container>
    <v-card id="activityView">
      <v-card-title>
{{ $t('activityfeed.title') }}
        <v-spacer />
          <v-btn v-if="hasDeletePermissions" color="red" @click="showDeleteHistoryConfirm">
{{ $t('activityfeed.delete-user-history') }}
</v-btn>
          <v-btn color="grey" @click="getActivity">
            <v-icon :disabled="loading">
mdi-refresh
</v-icon>
          </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          filterable
          :headers="headers"
          :items="activity"
          item-key="id"
          :loading="loading"
          :search="search"
        >
          <template v-slot:item.verb="{ item }">
            <v-chip :class="item.verb">
              <v-icon class="mr-2" :class="item.verb">
{{ icon(item) }}
</v-icon>
              {{ item.verb.toUpperCase() }}
            </v-chip>
          </template>
          <template v-slot:item.time="{ item }">
               {{ displayDate(item.time) }}
          </template>
          <template v-slot:item.message="{ item }">
            <v-expansion-panels no-border>
            <v-expansion-panel :key="item.id" no-border>
              <v-expansion-panel-header>
                <span v-html="item.message" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                {{ item.meta }}
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-row justify="center">
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('activityfeed.delete-users-entire-history') }}</v-card-title>
        <v-card-text>{{ $t('activityfeed.delete-history-warning') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="cancelDeleteHistory">
{{ $t('global.cancel') }}
</v-btn>
          <v-btn color="red" @click="confirmDeleteHistory">
Delete!
</v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-row>
  </v-container>
</template>


<script>
  import api from '@/helpers/api.js'
  import moment from 'moment'
  import { Users } from '@/helpers/users.js'

  export default {
    name: 'ActivityView',

    props: {
      user: {
        type: String,
        default: undefined,
      },
      classLearner: {
        type: String,
        default: undefined,
      },
      title: {
        type: String,
        default: 'Loading',
      },
      userName: {
        type: String,
        default: 'ga',
      },
    },

    data () {
      return {
        activity: undefined,
        loading: false,
        refreshList: true,
        headers: [
          {
            text: this.$t('activityfeed.time'),
            align: 'start',
            sortable: true,
            value: 'time',
          },
          { text: this.$t('activityfeed.type'), value: 'verb' },
          { text: this.$t('activityfeed.module'), value: 'module' },
          { text: this.$t('activityfeed.message'), value: 'message' },
        ],
        userDisplayName: this.$t('activityfeed.you'),
        search: undefined,
        userRecord: undefined,
        showDeleteDialog: false,
        momentHumanizeOptions: {
          thresholds: [
            { l: 'm', r: 45, d: 'a minute' },
            { l: 'h', r: 22, d: 'an hour' },
            { l: 'd', r: 26, d: 'a day' },
            { l: 'M', r: 11, d: 'a month' },
            { l: 'y', r: 1, d: 'a year' },
            { l: 'y', d: 'years' },
          ],
        },
      }
    },

    computed: {
      statusTitle () {
        return this.user ? this.$t('activityfeed.events-for-this-user', [this.user]) : this.$t('global.loading')
      },
      hasDeletePermissions () {
        return this.$store.getters.currentUserProfile.user.roles.includes('sysadmin')
      },
    },

    watch: {
      refreshList: async function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.refreshList = false
          this.loading = true
          await this.getActivity()
          this.loading = false
        }
      },
    },

    async created () {
      this.loading = true
      if (!this.user) {
        this.userDisplayName = this.$t('activityfeed.you')
      } else {
        this.userDisplayName = Users.GetDisplayName(this.user)
      }
      await Promise.all(this.getActivity(), Users.Sync())
      this.loading = false
    },

    methods: {
      showDeleteHistoryConfirm () {
        this.showDeleteDialog = true
      },
      async confirmDeleteHistory () {
        this.loading = true
        await api.deleteUserHistory(this.user)
        this.loading = false
        this.showDeleteDialog = false
      },
      cancelDeleteHistory () {
        this.showDeleteDialog = false
      },
      displayDate (timeStamp) {
        return moment(timeStamp._seconds * 1000).format('HH:mm DD/MM/YYYY')
      },
      displayDuration (secs) {
        return moment.utc(secs * 1000).format('mm:ss')
      },
      createMessage (event) {
        const displayName = event.user === this.$store.getters.currentUserProfile.user.id ? this.$t('activityfeed.you') : Users.GetDisplayName(event.user) ?? this.userName
        const meta = event.meta ? JSON.parse(event.meta) : null
        switch (event.verb) {
          case 'measured':
            return this.$t('activityfeed.measured-message', [displayName, Object.keys(meta.measures).length, this.displayDuration(meta.duration), event.subject])
          case 'embodied':
            return this.$t('activityfeed.embodied-message', [displayName, event.subject])
          case 'scored':
            return this.$t('activityfeed.scored-message', [displayName, Object.keys(meta.results).length, event.subject])
          case 'said':
            return this.$t('activityfeed.said-message', [displayName, event.subject, this.displayDuration(meta.duration)])
          case 'passed':
            return this.$t('activityfeed.passed-message', [displayName, event.subject, event.module])
          case 'started':
            return this.$t('activityfeed.started-message', [displayName, event.module])
          case 'completed':
            return this.$t('activityfeed.completed-message', [displayName, event.module])
          case 'loggedIn':
            return this.$t('activityfeed.loggedin-message', [displayName])
          case 'loggedOut':
            return this.$t('activityfeed.loggedout-message', [displayName])
          case 'conversed':
            return this.$t('activityfeed.conversed-message', [displayName, event.subject, this.displayDuration(meta.time)])
          case 'survey':
            return this.$t('activityfeed.survey-message', [displayName, event.subject, event.module])
          case 'rated':
            return this.$t('activityfeed.rated-message', [displayName, meta.text, event.subject, meta.rating, meta.max])
          case 'observed':
            return this.$t('activityfeed.observed-message', [displayName, meta.percentageCorrect, event.subject])
          case 'watched':
            return this.$t('activityfeed.watched-message', [displayName, event.subject])
          case 'segmented':
            return this.$t('activityfeed.segmented-message', [displayName, event.subject])
          case 'accepted':
            return this.$t('activityfeed.accepted-message', [displayName, event.subject])
          case 'feedbacked':
            return this.$t('activityfeed.feedbacked-message', [displayName, event.subject, meta.questionText])
        }
      },
      icon (event) {
        switch (event.verb) {
          case 'passed':
          case 'started':
            return 'mdi-information-outline'
          case 'completed':
            return 'mdi-check-circle-outline'
          case 'loggedIn':
          case 'loggedOut':
            return 'mdi-login-variant'
          case 'survey':
            return 'mdi-clipboard-outline'
        }
      },
      async getActivity () {
        this.loading = true
        // console.log('getActivity: classLearner ' + this.classLearner + ' user = ' + this.user)
        if (this.classLearner)
        {
          this.activity = await api.getEventsForClassLearner(this.classLearner)
        } else if (this.user) {
          this.activity = await api.getEventsForUser(this.user)
        } else {
          this.activity = await api.getEventsForLoggedInUser()
        }
        if (this.activity && Array.isArray(this.activity)) {
          for (let i = 0; i < this.activity.length; i++) {
            this.activity[i].message = this.createMessage(this.activity[i])
          }
        } else {
          this.activity = []
        }
        // console.log(JSON.stringify(this.activity))
        this.loading = false
      },
    },
  }
</script>

<style scoped>
.loggedIn,.loggedOut { background-color: rgb(216, 175, 39) !important; color: white; }
.started,.passed { background-color: grey !important; color: white; }
.completed { background-color: rgb(76, 175, 88) !important; color: white; }
.survey { background-color: rgb(81, 173, 216) !important; color: white; }
</style>
